import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { IconArrowUp } from './icons';

/**
 * ✨ EFFETS AU DÉFILEMENT (sobres, une seule fois, désactivés si l'appareil demande moins d'animations)
 * - RevealManager : fait apparaître en douceur chaque élément marqué data-reveal quand il entre à l'écran.
 *   Le contenu reste visible sans JavaScript, à l'impression et pour les moteurs de recherche :
 *   l'état « caché » n'est appliqué que lorsque le navigateur sait détecter l'entrée à l'écran.
 * - CountUp : un chiffre qui défile jusqu'à sa valeur (« 1 300 000 FCFA », « 8 », « 0 % »).
 * - BackToTop : bouton « Revenir en haut » sur les pages longues.
 */

const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
const canObserve = () => typeof window !== 'undefined' && 'IntersectionObserver' in window;

/** À placer une fois dans la mise en page */
export const RevealManager = () => {
  useEffect(() => {
    if (!canObserve() || reducedMotion()) return undefined;
    const root = document.documentElement;
    root.classList.add('reveal-armed');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    let frame = 0;
    const scan = () => {
      frame = 0;
      document.querySelectorAll('[data-reveal]:not([data-reveal-watched])').forEach(el => {
        el.setAttribute('data-reveal-watched', '');
        observer.observe(el);
      });
    };
    scan();
    // Nouvelles pages (navigation) : surveiller les éléments ajoutés, au plus une fois par image
    const mutations = new MutationObserver(() => { if (!frame) frame = window.requestAnimationFrame(scan); });
    mutations.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      mutations.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
      root.classList.remove('reveal-armed');
    };
  }, []);
  return null;
};

// « 1 300 000 FCFA » → préfixe, nombre, décimales, suffixe ; null si la valeur n'est pas chiffrée
const parseValue = (value) => {
  // Le nombre se termine par un chiffre : l'espace avant l'unité (« 100 000 FCFA ») reste dans le suffixe
  const match = String(value).match(/^(\D*?)(\d(?:[\d\s  ]*\d)?(?:,\d+)?)(.*)$/s);
  if (!match) return null;
  const digits = match[2].replace(/[\s  ]/g, '');
  const decimals = digits.includes(',') ? digits.split(',')[1].length : 0;
  return { prefix: match[1], number: parseFloat(digits.replace(',', '.')), decimals, suffix: match[3] };
};

/** Chiffre qui défile jusqu'à sa valeur quand il apparaît à l'écran (valeur finale exacte à la fin) */
export const CountUp = ({ value, duration = 1200 }) => {
  const ref = useRef(null);
  const parsed = parseValue(value);
  const [display, setDisplay] = useState(String(value));
  // Pas d'animation pour une fourchette (« 5 à 10 % ») : un seul nombre par valeur animée
  const animate = Boolean(parsed && parsed.number > 0 && !/\d/.test(parsed.suffix) && canObserve() && !reducedMotion());

  // Avant le premier affichage : partir de zéro (évite d'afficher la valeur puis de revenir à 0)
  useLayoutEffect(() => {
    if (animate) setDisplay(`${parsed.prefix}0${parsed.suffix}`);
    else setDisplay(String(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, animate]);

  useEffect(() => {
    if (!animate || !ref.current) return undefined;
    let frame = 0;
    const format = (n) => n.toLocaleString('fr-FR', { minimumFractionDigits: parsed.decimals, maximumFractionDigits: parsed.decimals });
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      const start = performance.now();
      const tick = (now) => {
        const progress = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        if (progress < 1) {
          setDisplay(`${parsed.prefix}${format(parsed.number * eased)}${parsed.suffix}`);
          frame = window.requestAnimationFrame(tick);
        } else {
          setDisplay(String(value));
        }
      };
      frame = window.requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    observer.observe(ref.current);
    return () => { observer.disconnect(); if (frame) window.cancelAnimationFrame(frame); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, animate, duration]);

  // Les lecteurs d'écran annoncent toujours la valeur finale
  return (
    <span ref={ref} aria-label={String(value)} className="tabular-nums">
      <span aria-hidden="true">{display}</span>
    </span>
  );
};

/** Bouton « Revenir en haut », visible après un peu de défilement */
export const BackToTop = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 700);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <button type="button" aria-label="Revenir en haut de la page" title="Revenir en haut"
      onClick={() => window.scrollTo({ top: 0, behavior: reducedMotion() ? 'auto' : 'smooth' })}
      className={`!fixed bottom-5 right-5 z-40 inline-flex h-12 w-12 items-center justify-center rounded-full bg-orange-500 text-white shadow-lg ring-4 ring-white/70 transition-all duration-300 hover:bg-orange-600 focus:outline-none focus-visible:ring-orange-300 print:hidden ${
        visible ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-4 opacity-0'
      }`}
      tabIndex={visible ? 0 : -1}>
      <IconArrowUp className="h-5 w-5" aria-hidden="true" />
    </button>
  );
};
